import { TFunction } from 'i18next';
import moment from 'moment';

export const SELF_IPO_CANCELLED_APPLICATION_STATUS = 5;

export const SELF_IPO_ARCHIVED_OFFERING_STATUS = 5;

export const VISIBLE_SELF_IPO_HOME_STATUSES = new Set([1, 2]);

export const formatSelfIpoMoneyAmount = (amount: number): string => {
  const truncated = Math.trunc(amount * 100) / 100;

  return truncated.toFixed(2);
};

export const formatSelfIpoPercent = (amount: number): string => {
  const truncated = Math.trunc(amount * 100) / 100;

  return truncated.toFixed(2);
};

export const formatSelfIpoDateTime = (date: Date, language: string): string => {
  if (language === 'ru') {
    return moment(date).format('DD.MM.YYYY HH:mm');
  }

  return moment(date).locale(language).format('L LT');
};

export const getSelfIpoApplicationStatusText = (
  t: TFunction,
  status: number
): string => {
  switch (status) {
    case 1:
      return t('selfIpoStatusSubmitted');
    case 2:
      return t('selfIpoStatusAllocated');
    case 3:
      return t('selfIpoStatusNotAllocated');
    case 4:
      return t('selfIpoStatusClosed');
    case 5:
      return t('selfIpoStatusCancelled');
    default:
      return String(status);
  }
};

export const getSelfIpoCurrentApplicationInfoRows = (
  t: TFunction,
  application: Pick<SelfIpoApplication, 'amount' | 'status'>
): Array<{ label: string; value: string }> => {
  return [
    {
      label: t('status'),
      value: getSelfIpoApplicationStatusText(t, application.status),
    },
    {
      label: t('selfIpoAmount'),
      value: `$${formatSelfIpoMoneyAmount(application.amount)}`,
    },
  ];
};

export const getSelfIpoEligibilityText = (
  t: TFunction,
  eligibility: SelfIpoEligibility
): string => {
  if (eligibility.message && eligibility.message.length > 0) {
    return eligibility.message;
  }

  switch (eligibility.status) {
    case 'ready':
      return t('selfIpoEligibilityReady');
    case 'no_portfolio':
      return t('selfIpoEligibilityNoPortfolio');
    case 'book_closed':
      return t('selfIpoEligibilityBookClosed');
    case 'insufficient_balance':
      return t('selfIpoEligibilityInsufficientBalance');
    case 'already_applied':
      return t('selfIpoEligibilityAlreadyApplied');
    default:
      return eligibility.status;
  }
};

export type SelfIpoDetailAction = 'apply' | 'apply_offering' | 'top_up';

export const getSelfIpoDetailAction = (
  eligibilityStatus: string
): SelfIpoDetailAction | null => {
  switch (eligibilityStatus) {
    case 'ready':
      return 'apply';
    case 'no_portfolio':
      return 'apply_offering';
    case 'insufficient_balance':
      return 'top_up';
    default:
      return null;
  }
};

export const canOpenOfferingFromApplication = (
  application: SelfIpoApplication
): boolean => {
  return (
    application.status !== SELF_IPO_CANCELLED_APPLICATION_STATUS &&
    application.offering?.status !== SELF_IPO_ARCHIVED_OFFERING_STATUS &&
    application.offering !== null
  );
};

export const isSelfIpoVisibleOnHome = (offering: SelfIpoOffering): boolean => {
  return VISIBLE_SELF_IPO_HOME_STATUSES.has(offering.status);
};
